/**
 * @velyn-custom — Manual play/pause + optional play-when-in-view for Vely video text overlay.
 * Play icon → play; click video while playing → pause + show play icon; repeat.
 * With data-play-in-view: muted play when scrolled into view, pause when scrolled away.
 */
class VelyVideoOverlayPlay extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector("[data-vely-vto-play]");
    this.thumb = this.querySelector(".video-thumbnail");
    this.video = this.querySelector("video");
    this.playInView = this.hasAttribute("data-play-in-view");
    this._enterTimer = null;
    this._inView = false;

    this.onPlayClick = this.onPlayClick.bind(this);
    this.onMediaClick = this.onMediaClick.bind(this);
    this.onPlay = this.onPlay.bind(this);
    this.onPause = this.onPause.bind(this);
    this.onInViewChange = this.onInViewChange.bind(this);

    if (this.button) {
      this.button.addEventListener("click", this.onPlayClick);
    }
    this.addEventListener("click", this.onMediaClick);

    if (this.video) {
      this.bindVideoEvents(this.video);
    }

    if (
      this.playInView &&
      !(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches)
    ) {
      this.setupInViewObserver();
    }
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener("click", this.onPlayClick);
    }
    this.removeEventListener("click", this.onMediaClick);
    this.unbindVideoEvents();
    this.teardownInViewObserver();
    if (this._enterTimer) {
      clearTimeout(this._enterTimer);
      this._enterTimer = null;
    }
  }

  setupInViewObserver() {
    if (this._inViewObserver) return;

    this._inViewObserver = new IntersectionObserver(this.onInViewChange, {
      threshold: [0, 0.2, 0.4, 0.6, 1],
      rootMargin: "0px 0px -8% 0px",
    });
    this._inViewObserver.observe(this);
  }

  teardownInViewObserver() {
    if (!this._inViewObserver) return;
    this._inViewObserver.disconnect();
    this._inViewObserver = null;
  }

  onInViewChange(entries) {
    const entry = entries[0];
    if (!entry) return;

    const shouldPlay = entry.isIntersecting && entry.intersectionRatio >= 0.4;

    if (shouldPlay) {
      this._inView = true;
      this.scheduleInViewPlay();
      return;
    }

    this._inView = false;
    if (this._enterTimer) {
      clearTimeout(this._enterTimer);
      this._enterTimer = null;
    }
    if (this.video && !this.video.paused) {
      this.video.pause();
    }
  }

  scheduleInViewPlay() {
    if (this._enterTimer) clearTimeout(this._enterTimer);

    // Soft delay so playback starts after the section settles in view
    this._enterTimer = setTimeout(() => {
      this._enterTimer = null;
      if (!this._inView) return;

      if (!this.video) {
        this.loadVideo().then((video) => {
          if (video && this._inView) this.safePlay(video);
        });
        return;
      }

      this.safePlay(this.video);
    }, 180);
  }

  bindVideoEvents(video) {
    this.unbindVideoEvents();
    this.video = video;
    video.addEventListener("play", this.onPlay);
    video.addEventListener("pause", this.onPause);
  }

  unbindVideoEvents() {
    if (!this.video) return;
    this.video.removeEventListener("play", this.onPlay);
    this.video.removeEventListener("pause", this.onPause);
  }

  onPlay() {
    this.classList.add("is-playing");
  }

  onPause() {
    this.classList.remove("is-playing");
  }

  onPlayClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.video) {
      this.loadVideo().then((video) => {
        if (video) this.safePlay(video);
      });
      return;
    }

    if (this.video.paused) {
      this.safePlay(this.video);
    }
  }

  onMediaClick(event) {
    if (event.target.closest("[data-vely-vto-play]")) return;
    if (!this.video || this.video.paused) return;

    event.preventDefault();
    event.stopPropagation();
    this.video.pause();
  }

  loadVideo() {
    if (this._loadPromise) return this._loadPromise;

    this._loadPromise = new Promise((resolve) => {
      const template = this.querySelector("template");
      if (!template) {
        resolve(null);
        return;
      }

      const content = document.createElement("div");
      content.appendChild(template.content.firstElementChild.cloneNode(true));

      const video = content.querySelector("video");
      if (!video) {
        resolve(null);
        return;
      }

      this.setAttribute("loaded", "true");
      video.setAttribute("playsinline", "");
      video.muted = true;
      video.playsInline = true;

      const dataSrc = video.dataset.src;
      if (dataSrc) {
        video.src = dataSrc;
        video.removeAttribute("data-src");
      }

      this.appendChild(video);
      this.bindVideoEvents(video);

      const finish = () => resolve(video);

      if (video.readyState >= 2) {
        finish();
      } else {
        video.addEventListener("loadeddata", finish, { once: true });
        video.load();
      }
    });

    return this._loadPromise;
  }

  safePlay(video) {
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch((err) => {
        if (err?.name !== "AbortError") {
          console.warn("vely-video-overlay play failed:", err);
        }
      });
    }
  }
}

if (!customElements.get("vely-video-overlay-play")) {
  customElements.define("vely-video-overlay-play", VelyVideoOverlayPlay);
}
